/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import CancelFill from '@accelint/icons/cancel-fill';
import Loop from '@accelint/icons/loop';
import Search from '@accelint/icons/search';
import {
  SearchField as AriaSearchField,
  Button,
  Input,
} from 'react-aria-components/SearchField';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { useContextProps } from 'react-aria-components/slots';
import { Icon } from '../icon';
import { IconProvider } from '../icon/context';
import { SearchFieldContext } from './context';
import styles from './styles.module.css';
import type { SearchFieldProps } from './types';

/**
 * SearchField - Search input with integrated search icon, loading state, and clear button
 *
 * Supports outline and filled variants with automatic icon sizing.
 *
 * @param props - {@link SearchFieldProps}
 * @param props.ref - Forwarded ref for the field container.
 * @param props.classNames - Custom CSS class names for field elements.
 * @param props.inputProps - Additional props for the input element.
 * @param props.isLoading - Whether to show loading spinner instead of clear button.
 * @param props.variant - Visual variant ('outline' or 'filled').
 * @returns The rendered SearchField component.
 *
 * @example
 * ```tsx
 * <SearchField
 *   label="Search"
 *   placeholder="Search items..."
 *   onChange={(value) => setQuery(value)}
 * />
 * ```
 */
export function SearchField({ ref, ...props }: SearchFieldProps) {
  [props, ref] = useContextProps(props, ref ?? null, SearchFieldContext);

  const {
    classNames,
    inputProps,
    isLoading = false,
    variant = 'outline',
    ...rest
  } = props;

  return (
    <IconProvider size='small'>
      <AriaSearchField
        {...rest}
        ref={ref}
        className={composeRenderProps(classNames?.field, (className) =>
          clsx('group/search-field', styles.field, styles[variant], className),
        )}
      >
        <Icon className={clsx(styles.search, classNames?.search)}>
          <Search />
        </Icon>
        <Input
          {...inputProps}
          className={composeRenderProps(classNames?.input, (className) =>
            clsx(styles.input, className),
          )}
          type='search'
        />
        {isLoading ? (
          <Icon className={clsx(styles.loading, classNames?.loading)}>
            <Loop />
          </Icon>
        ) : (
          <Button
            className={composeRenderProps(classNames?.clear, (className) =>
              clsx(styles.clear, className),
            )}
          >
            <Icon>
              <CancelFill />
            </Icon>
          </Button>
        )}
      </AriaSearchField>
    </IconProvider>
  );
}
